// ============================================================================
// api_webrtcBuffers.ts
// Gestion des buffers ICE et nettoyage des connexions mortes
// v2.3 — Logs console enrichis
// ============================================================================

import { SignalingRefs } from '../../dataTypes/liveStreamManagment/types_signaling';

// ============================================================================
// Traçabilité des fermetures de PeerConnection
//
// `track.onended` (api_webrtcPeerConnection) signale qu'une piste reçue d'un
// device s'est arrêtée, mais pas POURQUOI : fermer une PeerConnection termine
// ses pistes reçues, et quatre chemins de la régie la ferment — watchdog,
// reconnexion, Offer réémise par le device, nettoyage sur déconnexion. Vu du
// journal, les quatre sont indistinguables, et « la capture s'est arrêtée côté
// device » leur ressemble aussi.
//
// Chaque site enregistre donc son motif JUSTE AVANT `pc.close()`, et onended le
// relit. L'horodatage sert à écarter un motif trop ancien pour être la cause :
// au-delà de la fenêtre, la piste s'est arrêtée d'elle-même.
//
// Ce registre vit ici et non dans api_webrtcPeerConnection parce que
// cleanupDeadConnection est l'un des quatre sites : l'inverse créerait un cycle
// d'imports (api_webrtcPeerConnection importe déjà ce module).
// ============================================================================

const pcCloseCauses = new Map<string, { reason: string; at: number }>();

/** Fenêtre au-delà de laquelle un motif enregistré n'explique plus une piste
 *  qui s'arrête maintenant. Large : la fin de piste suit la fermeture de très
 *  près, mais peut passer par une tâche différée. */
const CLOSE_CAUSE_WINDOW_MS = 3000;

export const notePcCloseCause = (device_id: string, reason: string): void => {
  pcCloseCauses.set(device_id, { reason, at: Date.now() });
};

/**
 * Motif de la dernière fermeture de PC pour ce device, si elle est assez
 * récente pour expliquer un événement qui survient maintenant.
 * Sinon, une phrase qui dit explicitement qu'aucune fermeture régie n'est en
 * cause — c'est l'information la plus utile des deux.
 */
export const readPcCloseCause = (device_id: string): string => {
  const entry = pcCloseCauses.get(device_id);
  if (!entry) return 'aucune fermeture régie enregistrée — arrêt venu du device ou du réseau';
  const age = Date.now() - entry.at;
  if (age > CLOSE_CAUSE_WINDOW_MS) {
    return `aucune fermeture régie récente (dernière il y a ${age} ms : ${entry.reason}) `
         + '— arrêt venu du device ou du réseau';
  }
  return `${entry.reason} (il y a ${age} ms)`;
};

// ============================================================================
// Inventaire des PeerConnection vivantes
//
// La régie en ouvre jusqu'à QUATRE par appareil connecté — flux montant, flux
// retour (qui encode le canvas une seconde fois), et les deux sens de
// l'intercom — auxquelles s'ajoutent la sortie WHIP et, en SFU, un tirage WHEP
// par device. Quand la régie se fige à la connexion d'un téléphone, la question
// « combien sont réellement ouvertes, et dans quel état » est la première à
// laquelle il faut pouvoir répondre.
//
// La Map retient la PC elle-même : elle empêche donc le ramasse-miettes de la
// collecter. C'est sans conséquence parce que toute PC `closed` est retirée à la
// lecture suivante — l'inventaire se purge tout seul.
// ============================================================================

const trackedPcs = new Map<RTCPeerConnection, { kind: string; label: string }>();

/**
 * `label` dit DE QUI il s'agit — l'appareil, l'angle, le programme. Le `kind` seul
 * ne suffit plus depuis qu'on mesure les débits par tuyau : « montant=3 » ne dit
 * pas lequel des trois téléphones sature le lien.
 */
export const trackPeerConnection = (pc: RTCPeerConnection, kind: string, label = ''): void => {
  // Une connexion vivante du MÊME rôle pour le MÊME correspondant est un doublon :
  // deux encodeurs pour un seul flux, deux fois le débit montant. Constaté le
  // 25/08/2026 dans l'onglet Débits — « retour:emmanuel » deux fois, à 2,5 Mb/s et
  // 62 kb/s — après qu'un téléphone se soit mis en veille puis reconnecté.
  //
  // On le DIT plutôt que de fermer d'office : la fermeture appartient au code qui
  // tient la connexion, et une purge aveugle ici couperait une PC qu'il croit
  // encore sienne. Le nettoyage se fait explicitement par
  // `closeStrayPeerConnections`, à l'endroit qui sait ce qu'il remplace.
  if (label) {
    trackedPcs.forEach((suivi, ancienne) => {
      if (ancienne === pc) return;
      if (suivi.kind !== kind || suivi.label !== label) return;
      if (ancienne.connectionState === 'closed') return;
      console.warn(
        `[PC] 👥 ${kind}:${label} — une connexion précédente est encore ${ancienne.connectionState}`
        + ' au moment où une nouvelle est ouverte'
      );
    });
  }
  trackedPcs.set(pc, { kind, label });
};

/**
 * Ferme les connexions égarées d'un rôle et d'un correspondant donnés.
 *
 * Le cas visé : une PeerConnection qui a perdu sa place dans la Map de son module
 * — remplacée sans être fermée, ou orpheline d'un remontage — et que plus personne
 * ne fermera donc jamais. Elle continue d'émettre : c'est du lien montant dépensé
 * pour rien, et sur une prise multi-angle ce « rien » se retire de ce qui reste
 * aux angles.
 *
 * `sauf` protège celle qu'on vient de créer.
 */
export const closeStrayPeerConnections = (kind: string, label: string, sauf?: RTCPeerConnection): number => {
  let fermees = 0;
  trackedPcs.forEach((suivi, pc) => {
    if (pc === sauf) return;
    if (suivi.kind !== kind || suivi.label !== label) return;
    if (pc.connectionState === 'closed') { trackedPcs.delete(pc); return; }
    console.warn(`[PC] 🧹 ${kind}:${label} — connexion égarée (${pc.connectionState}) fermée`);
    notePcCloseCause(label, `closeStrayPeerConnections — ${kind} égaré (état ${pc.connectionState})`);
    try { pc.close(); } catch { /* déjà fermée */ }
    trackedPcs.delete(pc);
    fermees += 1;
  });
  return fermees;
};

/** Ex. : `montant:connected=2 retour:connecting=1 intercom:new=4` */
export const summarizeLivePeerConnections = (): string => {
  const counts = new Map<string, number>();
  trackedPcs.forEach(({ kind }, pc) => {
    if (pc.connectionState === 'closed') { trackedPcs.delete(pc); return; }
    const key = `${kind}:${pc.connectionState}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  if (counts.size === 0) return 'aucune';
  return Array.from(counts.entries()).map(([k, n]) => `${k}=${n}`).join(' ');
};

/**
 * L'inventaire, pour qui veut interroger chaque connexion — la mesure de débit,
 * aujourd'hui. Purge les fermées au passage, comme le résumé ci-dessus : c'est ce
 * qui empêche la Map de retenir indéfiniment des PeerConnection mortes.
 */
export const listTrackedPeerConnections = (): Array<{ pc: RTCPeerConnection; kind: string; label: string }> => {
  const vivantes: Array<{ pc: RTCPeerConnection; kind: string; label: string }> = [];
  trackedPcs.forEach(({ kind, label }, pc) => {
    // `failed` autant que `closed` : une connexion en échec ne se rétablit pas
    // d'elle-même — il faut un redémarrage ICE, et aucun acquéreur n'en fait ;
    // ils recréent une connexion. La garder ici, c'est la compter indéfiniment
    // parmi les tuyaux ouverts. `disconnected` est laissé, lui : celui-là se
    // rétablit tout seul, et le purger ferait clignoter l'inventaire.
    //
    // Une connexion `failed` est FERMÉE avant d'être oubliée : sortie de
    // l'inventaire sans être fermée, elle restait ouverte (et gardait son
    // allocation sur le relais TURN), et closeStrayPeerConnections, qui ne
    // parcourt que cet inventaire, ne pouvait plus jamais la rattraper.
    if (pc.connectionState === 'failed') {
      notePcCloseCause(label, `listTrackedPeerConnections — ${kind} en échec, fermé`);
      try { pc.close(); } catch { /* déjà fermée */ }
      trackedPcs.delete(pc);
      return;
    }
    if (pc.connectionState === 'closed') {
      trackedPcs.delete(pc);
      return;
    }
    vivantes.push({ pc, kind, label });
  });
  return vivantes;
};

export const cleanupDeviceBuffers = (
  device_id: string,
  refs: Pick<SignalingRefs, 'iceCandidateBufferRef' | 'remoteDescSetRef'>
): void => {
  const iceBufSize = refs.iceCandidateBufferRef.current.get(device_id)?.length ?? 0;
  console.log(`[BUFFERS] 🧹 cleanupDeviceBuffers: ${device_id} (${iceBufSize} ICE candidats supprimés)`);
  refs.iceCandidateBufferRef.current.delete(device_id);
  refs.remoteDescSetRef.current.delete(device_id);
};

export const cleanupDeadConnection = (
  device_id: string,
  refs: Pick<SignalingRefs, 'iceCandidateBufferRef' | 'remoteDescSetRef' | 'remoteConnectionsRef'>,
  callbacks: { setRemoteConnections: (updater: (prev: Map<string, any>) => Map<string, any>) => void }
): void => {
  const existing = refs.remoteConnectionsRef.current.get(device_id);
  if (existing) {
    const state = existing.peerConnection?.connectionState;
    console.warn(`[BUFFERS] 🧹 cleanupDeadConnection: ${device_id} (état PC: ${state ?? 'null'})`);
    notePcCloseCause(device_id, `cleanupDeadConnection — déconnexion ou nettoyage (état PC: ${state ?? 'null'})`);
    try {
      existing.peerConnection?.close();
    } catch (e) {
      console.warn(`[BUFFERS] Erreur fermeture PC ${device_id}:`, e);
    }
    refs.remoteConnectionsRef.current.delete(device_id);
    callbacks.setRemoteConnections(prev => {
      const next = new Map(prev);
      next.delete(device_id);
      return next;
    });
  } else {
    console.log(`[BUFFERS] 🧹 cleanupDeadConnection: ${device_id} (pas de PC existante)`);
  }
  cleanupDeviceBuffers(device_id, refs);
};

export const flushIceCandidateBuffer = async (
  device_id: string,
  pc: RTCPeerConnection,
  refs: Pick<SignalingRefs, 'iceCandidateBufferRef'>,
  callbacks: { addSdpLog: (entry: any) => void }
): Promise<void> => {
  const buf = refs.iceCandidateBufferRef.current.get(device_id) ?? [];
  if (!buf.length) return;

  console.log(`[BUFFERS] 🧊 flush ${buf.length} ICE candidat(s) bufférisé(s) pour ${device_id}`);
  callbacks.addSdpLog({
    direction: 'info', type: 'event',
    label: `🧊 Flush ${buf.length} ICE candidat(s) bufférisé(s)`,
    deviceId: device_id,
  });

  refs.iceCandidateBufferRef.current.set(device_id, []);
  for (const c of buf) {
    try {
      await pc.addIceCandidate(new RTCIceCandidate(c));
    } catch (err) {
      console.warn(`[BUFFERS] Erreur addIceCandidate ${device_id}:`, err);
    }
  }
};
